var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert, Snackbar, Grid, Card, CardContent, } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, GetApp as ExportIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { serviceHistoryService } from '@services/serviceHistoryService';
import { formatDate, formatDateForFilename } from '@utils/dateUtils';
import { machineService } from '@services/machineService';
import { businessService } from '@services/businessService';
import { useAuth } from '@contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import ServiceHistoryModal from './ServiceHistoryModal';
const ServiceHistory = ({ hideTitle = false }) => {
    const { isAdmin, user } = useAuth();
    const { machineId } = useParams();
    const navigate = useNavigate();
    const [serviceHistories, setServiceHistories] = useState([]);
    const [businesses, setBusinesses] = useState([]);
    const [machines, setMachines] = useState([]);
    const [allMachines, setAllMachines] = useState([]);
    const [selectedBusinessId, setSelectedBusinessId] = useState('');
    const [selectedMachineId, setSelectedMachineId] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedServiceHistory, setSelectedServiceHistory] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [serviceHistoryToDelete, setServiceHistoryToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [adminToastShown, setAdminToastShown] = useState(false);
    useEffect(() => {
        loadBusinesses();
    }, []);
    useEffect(() => {
        // If we're viewing from a specific machine, set it up
        if (machineId) {
            const numMachineId = parseInt(machineId, 10);
            if (!isNaN(numMachineId)) {
                setSelectedMachineId(numMachineId);
                // Load just this machine's data
                loadMachineById(numMachineId);
            }
        }
    }, [machineId]);
    useEffect(() => {
        // Show admin notification toast on mount
        if (isAdmin && !adminToastShown) {
            setSnackbar({
                open: true,
                message: 'You are currently an admin user and can see all businesses, machines, service histories and jobs for all users',
                severity: 'info',
            });
            setAdminToastShown(true);
        }
    }, [isAdmin, adminToastShown]);
    useEffect(() => {
        if (businesses.length > 0 && !machineId) {
            loadMachines();
        }
    }, [selectedBusinessId, businesses]);
    useEffect(() => {
        if (machines.length > 0 || machineId) {
            loadServiceHistories();
        }
    }, [selectedMachineId, machines]);
    const loadBusinesses = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const data = yield businessService.getAll();
            setBusinesses(data);
            // Set to user's default business if not viewing a specific machine
            if (data.length > 0 && !machineId) {
                if (user === null || user === void 0 ? void 0 : user.defaultBusinessId) {
                    setSelectedBusinessId(user.defaultBusinessId);
                }
                else {
                    setSelectedBusinessId(data[0].id);
                }
            }
        }
        catch (error) {
            console.error('Error loading businesses:', error);
            showSnackbar('Error loading businesses', 'error');
        }
    });
    const loadMachineById = (id) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const machine = yield machineService.getById(id);
            setAllMachines([machine]);
            setMachines([machine]);
            setSelectedMachineId(machine.id);
            setSelectedBusinessId(machine.businessId);
        }
        catch (error) {
            console.error('Error loading machine:', error);
            showSnackbar('Error loading machine', 'error');
        }
    });
    const loadMachines = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            let data;
            if (selectedBusinessId === '') {
                data = yield machineService.getAll();
            }
            else {
                data = yield machineService.getAll(selectedBusinessId);
            }
            setAllMachines(data);
            setMachines(data);
            // Auto-select first machine if available
            if (data.length > 0) {
                setSelectedMachineId(data[0].id);
            }
            else {
                setSelectedMachineId('');
            }
        }
        catch (error) {
            console.error('Error loading machines:', error);
            showSnackbar('Error loading machines', 'error');
        }
    });
    const loadServiceHistories = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            let data;
            if (selectedMachineId === '') {
                data = yield serviceHistoryService.getAll();
            }
            else {
                data = yield serviceHistoryService.getByMachineId(selectedMachineId);
            }
            setServiceHistories(data);
        }
        catch (error) {
            console.error('Error loading service histories:', error);
            showSnackbar('Error loading service histories', 'error');
        }
        finally {
            setLoading(false);
        }
    });
    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };
    const handleCloseSnackbar = () => {
        setSnackbar(Object.assign(Object.assign({}, snackbar), { open: false }));
    };
    const handleOpenModal = (serviceHistory) => {
        setSelectedServiceHistory(serviceHistory || null);
        setModalOpen(true);
    };
    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedServiceHistory(null);
    };
    const handleSaveServiceHistory = (serviceHistoryData) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            if (selectedServiceHistory) {
                const dataWithIds = Object.assign(Object.assign({}, serviceHistoryData), { id: selectedServiceHistory.id, machineId: selectedServiceHistory.machineId });
                yield serviceHistoryService.update(selectedServiceHistory.id, dataWithIds);
                showSnackbar('Service history updated successfully', 'success');
            }
            else {
                // Set the machine ID if creating new service history
                const dataWithMachine = Object.assign(Object.assign({}, serviceHistoryData), { machineId: selectedMachineId || ((_a = machines[0]) === null || _a === void 0 ? void 0 : _a.id) });
                yield serviceHistoryService.create(dataWithMachine);
                showSnackbar('Service history created successfully', 'success');
            }
            handleCloseModal();
            loadServiceHistories();
        }
        catch (error) {
            console.error('Error saving service history:', error);
            showSnackbar('Error saving service history', 'error');
        }
    });
    const handleOpenDeleteDialog = (serviceHistory) => {
        setServiceHistoryToDelete(serviceHistory);
        setDeleteDialogOpen(true);
    };
    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setServiceHistoryToDelete(null);
    };
    const handleDeleteServiceHistory = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!serviceHistoryToDelete)
            return;
        try {
            yield serviceHistoryService.delete(serviceHistoryToDelete.id);
            showSnackbar('Service history deleted successfully', 'success');
            handleCloseDeleteDialog();
            loadServiceHistories();
        }
        catch (error) {
            console.error('Error deleting service history:', error);
            showSnackbar('Error deleting service history', 'error');
        }
    });
    const handleExportToExcel = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (serviceHistories.length === 0) {
            showSnackbar('No service histories to export', 'error');
            return;
        }
        try {
            const machineName = ((_a = machines.find(m => m.id === selectedMachineId)) === null || _a === void 0 ? void 0 : _a.name) || 'All';
            const filename = `ServiceHistory_${machineName}_${formatDateForFilename()}.xlsx`;
            // Call API to get Excel file
            const blob = yield serviceHistoryService.exportToExcel(selectedMachineId);
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            showSnackbar('Service history exported successfully', 'success');
        }
        catch (error) {
            console.error('Error exporting service history:', error);
            showSnackbar('Error exporting service history', 'error');
        }
    });
    return (_jsxs(Box, { children: [machineId && (_jsx(Button, { variant: "outlined", startIcon: _jsx(BackIcon, {}), onClick: () => navigate(`/machines/${machineId}`), sx: { mb: 2 }, children: "Back to Machine" })), !hideTitle && (_jsx(Typography, { variant: "h5", sx: { mb: 2 }, children: "Service History" })), _jsx(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }, children: _jsxs(Box, { sx: { display: 'flex', gap: 2 }, children: [_jsx(Button, { variant: "outlined", startIcon: _jsx(ExportIcon, {}), onClick: handleExportToExcel, disabled: serviceHistories.length === 0, children: "Export" }), _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: () => handleOpenModal(), disabled: machines.length === 0, children: "Add Service Record" })] }) }), loading ? (_jsx(Typography, { children: "Loading service histories..." })) : machines.length === 0 ? (_jsx(Alert, { severity: "info", children: "No machines found. Please create a machine first before adding service records." })) : (_jsx(Grid, { container: true, spacing: 2, children: serviceHistories
                    .filter((service) => !selectedMachineId || service.machineId === selectedMachineId)
                    .map((service) => (_jsx(Grid, { item: true, xs: 12, children: _jsx(Card, { sx: {
                            cursor: 'pointer',
                            '&:hover': {
                                boxShadow: 6,
                                transform: 'translateY(-2px)',
                                transition: 'all 0.2s ease-in-out',
                            },
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }, onClick: () => handleOpenModal(service), children: _jsx(CardContent, { sx: { flexGrow: 1 }, children: _jsxs(Grid, { container: true, spacing: 2, alignItems: "center", children: [_jsxs(Grid, { item: true, xs: 12, sm: 3, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: "Service Date" }), _jsx(Typography, { variant: "body1", fontWeight: "bold", children: formatDate(service.serviceDate) })] }), _jsxs(Grid, { item: true, xs: 12, sm: 2, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: "Hours" }), _jsx(Typography, { variant: "body1", fontWeight: "bold", children: service.hours || '-' })] }), _jsxs(Grid, { item: true, xs: 12, sm: 5, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: "Service" }), _jsx(Typography, { variant: "body1", children: service.description })] }), _jsx(Grid, { item: true, xs: 12, sm: 2, children: _jsxs(Box, { sx: { display: 'flex', gap: 1, justifyContent: 'flex-end' }, children: [_jsx(Button, { size: "small", color: "primary", onClick: (e) => {
                                                        e.stopPropagation();
                                                        handleOpenModal(service);
                                                    }, sx: { minWidth: 'auto', px: 1 }, children: _jsx(EditIcon, { fontSize: "small" }) }), _jsx(Button, { size: "small", color: "error", onClick: (e) => {
                                                        e.stopPropagation();
                                                        handleOpenDeleteDialog(service);
                                                    }, sx: { minWidth: 'auto', px: 1 }, children: _jsx(DeleteIcon, { fontSize: "small" }) })] }) })] }) }) }) }, service.id))) })), _jsx(ServiceHistoryModal, { open: modalOpen, serviceHistory: selectedServiceHistory, onClose: handleCloseModal, onSave: handleSaveServiceHistory }), _jsxs(Dialog, { open: deleteDialogOpen, onClose: handleCloseDeleteDialog, children: [_jsx(DialogTitle, { children: "Confirm Delete" }), _jsx(DialogContent, { children: _jsx(DialogContentText, { children: "Are you sure you want to delete this service record? This action cannot be undone." }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: handleCloseDeleteDialog, children: "Cancel" }), _jsx(Button, { onClick: handleDeleteServiceHistory, color: "error", autoFocus: true, children: "Delete" })] })] }), _jsx(Snackbar, { open: snackbar.open, autoHideDuration: 6000, onClose: handleCloseSnackbar, anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, children: _jsx(Alert, { onClose: handleCloseSnackbar, severity: snackbar.severity, sx: { width: '100%' }, children: snackbar.message }) })] }));
};
export default ServiceHistory;
